import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  CheckCircle2,
  XCircle,
  Download,
  ShieldAlert,
  BookOpen,
  Wrench,
  Cog,
  Cloud,
  ClipboardList,
  Info,
} from "lucide-react";
import { toast } from "sonner";

import Header from "../components/Header";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Switch } from "../components/ui/switch";
import { Label } from "../components/ui/label";
import { useWizard } from "../state/wizard";
import api from "../lib/api";

const PART_META = {
  1: { title: "Part 1 - General", icon: BookOpen },
  2: { title: "Part 2 - Functional Descriptions", icon: Cog },
  3: { title: "Part 3 - Products", icon: Wrench },
  4: { title: "Part 4 - Digital Services", icon: Cloud },
};

const FORMATS = [
  { key: "pdf", label: "PDF only" },
  { key: "docx", label: "Word only" },
  { key: "both", label: "PDF + Word" },
];

export default function ResultsPage() {
  const nav = useNavigate();
  const { inputs, result } = useWizard();
  const [downloading, setDownloading] = useState(false);
  const [format, setFormat] = useState("pdf"); // pdf | docx | both
  const [branded, setBranded] = useState(true);

  useEffect(() => {
    if (!result) {
      nav("/wizard");
    } else {
      window.scrollTo({ top: 0, behavior: "auto" });
    }
  }, [result, nav]);

  const grouped = result?.grouped_clauses || {};
  const clauseCount = useMemo(
    () => (result?.selected_clauses || []).length,
    [result]
  );

  const downloadOne = async (endpoint, ext) => {
    const res = await api.post(
      endpoint,
      { inputs, branded },
      { responseType: "blob" }
    );
    const mime =
      ext === "pdf"
        ? "application/pdf"
        : "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
    const blob = new Blob([res.data], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const safe = (inputs.project_name || "specification").replace(/[^\w-]+/g, "_");
    const suffix = branded ? "_Helvar" : "";
    a.download = `Specification_${safe}${suffix}.${ext}`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      if (format === "pdf" || format === "both") {
        await downloadOne("/spec/pdf", "pdf");
      }
      if (format === "docx" || format === "both") {
        await downloadOne("/spec/docx", "docx");
      }
      toast.success("Specification downloaded", {
        description:
          format === "both"
            ? "Both PDF and Word copies have been saved to your device."
            : `A ${format === "pdf" ? "PDF" : "Word"} copy has been saved to your device.`,
      });
    } catch (e) {
      toast.error("Download failed", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally {
      setDownloading(false);
    }
  };

  if (!result) return null;

  return (
    <div data-testid="results-page" className="min-h-screen flex flex-col bg-white">
      <Header />

      <div className="max-w-7xl w-full mx-auto px-6 lg:px-10 pt-12 pb-4">
        <div className="flex flex-wrap items-center gap-3 text-[11px] uppercase tracking-[0.22em] font-semibold text-[#c80f3f]">
          <span>Generated Specification</span>
          <span className="text-[#4b5563]">·</span>
          <span className="text-[#4b5563] font-mono">
            {clauseCount} clauses assembled
          </span>
        </div>
        <div className="mt-2 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1f2a44] tracking-tight">
            {inputs.project_name || "Specification"}
          </h1>

          <DownloadPanel
            format={format}
            setFormat={setFormat}
            branded={branded}
            setBranded={setBranded}
            downloading={downloading}
            onDownload={handleDownload}
          />
        </div>
      </div>

      <div className="flex-1 max-w-7xl w-full mx-auto px-6 lg:px-10 pt-6 pb-12 grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* SIDEBAR */}
        <aside className="lg:col-span-4 space-y-4">
          <ProjectSummaryCard inputs={inputs} />
          <IncludedFeaturesCard items={result.included_features} />
          <ExcludedFeaturesCard items={result.excluded_features} />
          {(result.security_notes || []).length > 0 && (
            <SecurityNotesCard notes={result.security_notes} />
          )}
        </aside>

        {/* MAIN */}
        <main className="lg:col-span-8">
          <Card className="border border-[#e5e7eb] rounded-sm shadow-none mb-6">
            <div className="p-6 sm:p-8">
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] font-semibold text-[#c80f3f]">
                <ClipboardList className="h-3.5 w-3.5" strokeWidth={1.75} />
                Specification Summary
              </div>
              <p className="mt-4 text-base sm:text-lg text-[#1f2937] leading-relaxed">
                {result.summary}
              </p>
            </div>
          </Card>

          {[1, 2, 3, 4].map((n) => {
            const clauses = grouped[String(n)] || grouped[n] || [];
            if (!clauses.length) return null;
            return <PartBlock key={n} part={n} clauses={clauses} />;
          })}

          <PartHeader title="Part 5 - Glossary" icon={BookOpen} />
          <Card className="border border-[#e5e7eb] rounded-sm shadow-none">
            <div className="p-6 sm:p-8 space-y-5">
              {(result.glossary?.segment_terms || []).map((entry) => (
                <div key={entry.term}>
                  <div className="font-display font-semibold text-[#1f2a44]">
                    {entry.term}
                  </div>
                  <p className="mt-1 text-sm text-[#1f2937] leading-relaxed">
                    {entry.definition}
                  </p>
                </div>
              ))}

              {(result.glossary?.application_terms || []).length > 0 && (
                <>
                  <div className="pt-2 border-t border-[#e5e7eb] text-[11px] uppercase tracking-[0.22em] font-semibold text-[#4b5563]">
                    Application Definitions
                  </div>
                  {(result.glossary?.application_terms || []).map((entry) => (
                    <div key={entry.term}>
                      <div className="font-display text-sm font-semibold text-[#1f2a44]">
                        {entry.term}
                      </div>
                      <p className="mt-1 text-sm text-[#1f2937] leading-relaxed">
                        {entry.definition}
                      </p>
                    </div>
                  ))}
                </>
              )}
            </div>
          </Card>

          <div className="mt-10">
            <Button
              variant="ghost"
              onClick={() => nav("/wizard")}
              data-testid="results-back-button"
              className="text-[#1f2a44] hover:bg-[#f4f5f7] rounded-sm"
            >
              <ArrowLeft className="mr-2 h-4 w-4" strokeWidth={1.75} />
              Adjust inputs
            </Button>
          </div>
        </main>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Download panel: format select + branded toggle + big Download button
// ---------------------------------------------------------------------------
function DownloadPanel({ format, setFormat, branded, setBranded, downloading, onDownload }) {
  return (
    <div
      data-testid="download-panel"
      className="w-full lg:w-auto border border-[#e5e7eb] rounded-sm p-4 sm:p-5 bg-white"
    >
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#4b5563] mb-2">
            File format
          </div>
          <div
            role="tablist"
            className="inline-flex border border-[#e5e7eb] rounded-sm overflow-hidden"
          >
            {FORMATS.map((f) => {
              const active = format === f.key;
              return (
                <button
                  key={f.key}
                  type="button"
                  role="tab"
                  onClick={() => setFormat(f.key)}
                  data-testid={`format-${f.key}`}
                  className={`text-[11px] sm:text-xs uppercase tracking-[0.16em] font-semibold px-3 py-2 transition-colors border-r last:border-r-0 border-[#e5e7eb] ${
                    active
                      ? "bg-[#1f2a44] text-white"
                      : "bg-white text-[#4b5563] hover:text-[#1f2a44]"
                  }`}
                >
                  {f.label}
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#4b5563] mb-2">
            Version
          </div>
          <div className="inline-flex items-center gap-3">
            <Switch
              id="branded-toggle"
              checked={branded}
              onCheckedChange={setBranded}
              data-testid="branded-toggle"
              className="data-[state=checked]:bg-[#c80f3f]"
            />
            <Label
              htmlFor="branded-toggle"
              className="text-xs text-[#1f2937] font-medium cursor-pointer select-none"
            >
              {branded ? "Helvar-branded" : "Unbranded"}
            </Label>
          </div>
        </div>

        <Button
          onClick={onDownload}
          disabled={downloading}
          data-testid="download-open-button"
          className="bg-[#c80f3f] hover:bg-[#a10c32] text-white rounded-sm px-5 py-5 ml-auto disabled:opacity-70"
        >
          <Download className="mr-2 h-4 w-4" strokeWidth={1.75} />
          {downloading ? "Preparing…" : "Download"}
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components (unchanged from previous version)
// ---------------------------------------------------------------------------

function ProjectSummaryCard({ inputs }) {
  const subSegmentName = inputs.sub_segment_name || inputs.sub_segment || "";
  return (
    <Card
      data-testid="project-summary-card"
      className="border border-[#e5e7eb] rounded-sm shadow-none"
    >
      <div className="border-b border-[#e5e7eb] px-5 py-3 flex items-center justify-between">
        <span className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[#4b5563]">
          Project Summary
        </span>
      </div>
      <dl className="px-5 py-4 text-sm">
        <SummaryRow k="Project" v={inputs.project_name} />
        <SummaryRow k="Client" v={inputs.client_name || "-"} />
        <SummaryRow k="Country" v={inputs.country} />
        <SummaryRow k="Project Type" v={inputs.project_type} />
        <SummaryRow k="Segment" v={inputs.segment} accent />
        {subSegmentName && (
          <SummaryRow k="Sub-Segment" v={subSegmentName} accent />
        )}
        <SummaryRow k="Architecture" v={inputs.architecture} accent />
      </dl>
    </Card>
  );
}

function SummaryRow({ k, v, accent }) {
  return (
    <div className="flex items-baseline justify-between border-b border-dashed border-[#e5e7eb] py-2 last:border-b-0">
      <dt className="text-[11px] uppercase tracking-[0.16em] text-[#4b5563]">
        {k}
      </dt>
      <dd
        className={`text-sm font-medium text-right ${
          accent ? "text-[#c80f3f]" : "text-[#1f2937]"
        }`}
      >
        {v || "-"}
      </dd>
    </div>
  );
}

function IncludedFeaturesCard({ items }) {
  return (
    <Card
      data-testid="included-features-card"
      className="border border-[#e5e7eb] rounded-sm shadow-none"
    >
      <div className="border-b border-[#e5e7eb] px-5 py-3 flex items-center gap-2">
        <CheckCircle2 className="h-3.5 w-3.5 text-[#1f2a44]" strokeWidth={1.75} />
        <span className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[#1f2a44]">
          Included Features
        </span>
      </div>
      <ul className="px-5 py-4 space-y-2 text-sm">
        {items.length === 0 && (
          <li className="text-[#4b5563] italic">No optional features selected.</li>
        )}
        {items.map((f) => (
          <li
            key={f.slug}
            data-testid={`included-${f.slug}`}
            className="flex items-center gap-2 text-[#1f2937]"
          >
            <CheckCircle2 className="h-4 w-4 text-[#1f2a44]" strokeWidth={1.5} />
            {f.name}
          </li>
        ))}
      </ul>
    </Card>
  );
}

function ExcludedFeaturesCard({ items }) {
  return (
    <Card
      data-testid="excluded-features-card"
      className="border border-[#e5e7eb] rounded-sm shadow-none"
    >
      <div className="border-b border-[#e5e7eb] px-5 py-3 flex items-center gap-2">
        <XCircle className="h-3.5 w-3.5 text-[#4b5563]" strokeWidth={1.75} />
        <span className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[#4b5563]">
          Excluded Features
        </span>
      </div>
      <ul className="px-5 py-4 space-y-2 text-sm">
        {items.length === 0 && (
          <li className="text-[#4b5563] italic">All features selected.</li>
        )}
        {items.map((f) => (
          <li
            key={f.slug}
            data-testid={`excluded-${f.slug}`}
            className="flex items-start gap-2 text-[#4b5563]"
          >
            <XCircle className="h-4 w-4 mt-0.5" strokeWidth={1.5} />
            <div>
              <div className="text-[#1f2937]">{f.name}</div>
              <div className="text-xs">Reason: {f.reason}</div>
            </div>
          </li>
        ))}
      </ul>
    </Card>
  );
}

function SecurityNotesCard({ notes }) {
  return (
    <Card
      data-testid="security-notes-card"
      className="border border-[#c80f3f]/40 bg-[#c80f3f]/5 rounded-sm shadow-none"
    >
      <div className="border-b border-[#c80f3f]/30 px-5 py-3 flex items-center gap-2">
        <ShieldAlert className="h-3.5 w-3.5 text-[#c80f3f]" strokeWidth={1.75} />
        <span className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[#c80f3f]">
          Security & Approval Notes
        </span>
      </div>
      <ul className="px-5 py-4 space-y-2 text-sm text-[#1f2937]">
        {notes.map((n) => (
          <li key={n} className="flex gap-2">
            <Info className="h-4 w-4 mt-0.5 text-[#c80f3f]" strokeWidth={1.5} />
            <span>{n}</span>
          </li>
        ))}
      </ul>
    </Card>
  );
}

function PartHeader({ title, icon: Icon }) {
  return (
    <div className="mt-8 mb-3 flex items-center gap-2">
      <Icon className="h-4 w-4 text-[#c80f3f]" strokeWidth={1.75} />
      <h2 className="font-display text-xl sm:text-2xl font-bold text-[#1f2a44] tracking-tight">
        {title}
      </h2>
      <div className="flex-1 h-px bg-[#e5e7eb] ml-3" />
    </div>
  );
}

function PartBlock({ part, clauses }) {
  const { title, icon } = PART_META[part];
  const grouped = {};
  clauses.forEach((c) => {
    if (!grouped[c.section]) grouped[c.section] = [];
    grouped[c.section].push(c);
  });
  return (
    <div data-testid={`part-block-${part}`}>
      <PartHeader title={title} icon={icon} />
      <div className="space-y-4">
        {Object.entries(grouped).map(([section, list]) => (
          <div key={section}>
            {part !== 2 && (
              <div className="mt-2 mb-2 text-[11px] uppercase tracking-[0.22em] font-semibold text-[#4b5563]">
                {section}
              </div>
            )}
            <div className="space-y-3">
              {list.map((c) => (
                <ClauseCard key={c.id} clause={c} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ClauseCard({ clause }) {
  return (
    <Card
      data-testid={`clause-${clause.id}`}
      className="border border-[#e5e7eb] rounded-sm shadow-none spec-paper"
    >
      <div className="p-5 sm:p-6">
        <div className="flex items-center gap-3 flex-wrap">
          <span className="font-mono text-[11px] font-semibold text-[#c80f3f] tracking-tight">
            {clause.id}
          </span>
          <span className="h-3 w-px bg-[#e5e7eb]" />
          <span className="font-display text-sm sm:text-base font-semibold text-[#1f2a44]">
            {clause.title}
          </span>
        </div>
        <p className="mt-3 clause-body">{clause.text}</p>
        {clause.product_codes && (
          <p
            data-testid={`clause-product-codes-${clause.id}`}
            className="mt-3 clause-body text-[#4b5563] italic"
          >
            {clause.product_codes}
          </p>
        )}
      </div>
    </Card>
  );
}
